import React from "react";
import {
  // MUI 7 এ সরাসরি Grid ইমপোর্ট করলে সেটি Grid2 হিসেবেই কাজ করে
  Grid, 
  Typography,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
  Stack,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Favorite, Gavel, BorderColor } from "@mui/icons-material";
import { SignatureField } from "../../../../../../CustomForm";
import MultiSelect from "../../../../../../FormFields/CustomSelect/MultiSelect";

const relevantCircumstancesOptions = [
  { value: "All relevant circumstances considered", label: "All relevant circumstances considered" },
  { value: "Past and present wishes/statements/beliefs/values considered", label: "Past and present wishes/statements/beliefs/values considered" },
  { value: "Advance Decision consulted", label: "Advance Decision consulted" },
  { value: "Others consulted e.g. spouse/relatives/carer/attorney", label: "Others consulted e.g. spouse/relatives/carer/attorney" },
  { value: "Achieving the same result in a less restrictive way considered", label: "Achieving the same result in a less restrictive way considered" },
  { value: "Waiting until they can make the decision themselves considered", label: "Waiting until they can make the decision themselves considered" },
  { value: "Encouragement given to participate in the decision", label: "Encouragement given to participate in the decision" },
  { value: "The decision was not based solely on age/appearance/behaviour/condition", label: "The decision was not based solely on age/appearance/behaviour/condition" },
  { value: "In the case of life sustaining treatment - decision not made on desire to bring about death", label: "In the case of life sustaining treatment - decision not made on desire to bring about death" },
  { value: "Other (record below)", label: "Other (record below)" },
];

const BestInterestsSection = ({ formData, handleChange, decisionMakers, loadingMakers }) => {
  
  // JSON Parsing logic
  const relevantCircumstances = Array.isArray(formData.relevant_circumstances)
    ? formData.relevant_circumstances
    : JSON.parse(formData.relevant_circumstances || "[]");

  const selectedCircumstances = relevantCircumstances.map((val) => ({
    value: val,
    label: val,
  }));

  const handleMultiSelectChange = (selected) => {
    handleChange("relevant_circumstances", selected ? selected.map((s) => s.value) : []);
  };

  const inputStyle = {
    "& .MuiFilledInput-root": { 
      bgcolor: "#f8fafc", 
      borderRadius: "14px", 
      border: "1px solid #f1f5f9",
      transition: "0.2s",
      "&:hover": { bgcolor: "#f1f5f9" },
      "&.Mui-focused": { bgcolor: "#fff", borderColor: "error.main" }
    },
    "& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after": { 
      borderBottom: "none !important" 
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 900, color: "#ef4444", display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ p: 0.8, bgcolor: '#fee2e2', borderRadius: '8px', display: 'flex' }}>
          <Favorite sx={{ fontSize: 16 }} />
        </Box> 
        BEST INTERESTS ASSESSMENT / FURTHER DETAILS
      </Typography>

      <Grid container spacing={3}>
        {/* MUI 7 এ size={} প্রপ ব্যবহার করুন */}
        <Grid size={12}>
          <TextField
            fullWidth multiline rows={2} variant="filled"
            label="Steps taken to appoint an IMCA (if applicable)"
            value={formData.imca_steps || ""}
            onChange={(e) => handleChange("imca_steps", e.target.value)}
            sx={inputStyle}
          />
        </Grid>

        <Grid size={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: "20px", border: "1px solid #e2e8f0", bgcolor: "#fff" }}>
            <Typography variant="caption" sx={{ mb: 1.5, fontWeight: 900, color: "#64748b", display: 'block', textTransform: 'uppercase' }}>
              Relevant circumstances considered
            </Typography>
            <MultiSelect
              options={relevantCircumstancesOptions}
              value={selectedCircumstances}
              onChange={handleMultiSelectChange}
              placeholder="Select from options..."
            />
          </Paper>
        </Grid>

        {[
          { field: "options_balance", label: "Balance of options (Benefit vs Risk)" },
          { field: "outcome_reason", label: "Final outcome and reason for the decision" },
          { field: "disagreement_record", label: "Record of any disagreements & how handled" },
        ].map(({ field, label }) => (
          <Grid size={12} key={field}>
            <TextField
              fullWidth multiline rows={2} variant="filled"
              label={label}
              value={formData[field] || ""}
              onChange={(e) => handleChange(field, e.target.value)}
              sx={inputStyle}
            />
          </Grid>
        ))}

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, bgcolor: "#f8fafc", borderRadius: "18px", border: "1px solid #e2e8f0", height: '100%' }}>
             <FormControlLabel
                control={<Switch checked={formData.restrictions_dol_needed || false} onChange={(e) => handleChange("restrictions_dol_needed", e.target.checked)} color="primary" />}
                label={<Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: '#334155' }}>DoLS application required?</Typography>}
             />
             <TextField
                fullWidth multiline rows={2} variant="standard"
                placeholder="Details of restrictions or application status..."
                value={formData.restrictions_dol || ""}
                onChange={(e) => handleChange("restrictions_dol", e.target.value)}
                sx={{ mt: 2, "& .MuiInputBase-input": { fontSize: '0.85rem' } }}
             />
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, bgcolor: "#fff5f5", borderRadius: "18px", border: "1px solid #fee2e2", height: '100%' }}>
             <FormControlLabel
                control={<Switch checked={formData.risks_identified_flag || false} onChange={(e) => handleChange("risks_identified_flag", e.target.checked)} color="error" />}
                label={<Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#b91c1c" }}>Risks identified?</Typography>}
             />
             <TextField
                fullWidth multiline rows={2} variant="standard"
                placeholder="Describe risks to be managed..."
                value={formData.risks_identified || ""}
                onChange={(e) => handleChange("risks_identified", e.target.value)}
                sx={{ mt: 2, "& .MuiInputBase-input": { fontSize: '0.85rem' } }}
             />
          </Paper>
        </Grid>

        <Grid size={12}>
          <Paper elevation={0} sx={{ p: 4, bgcolor: "#f1f5f9", borderRadius: "24px" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: "#1e293b" }}>
                <Gavel fontSize="small" /> DECISION MAKER ATTESTATION
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                {loadingMakers ? (
                  <Stack direction="row" spacing={1} sx={{ p: 2 }}><CircularProgress size={18} /><Typography variant="caption">Loading...</Typography></Stack>
                ) : (
                  <TextField
                    select fullWidth label="Decision Maker" variant="outlined"
                    value={formData.decision_maker || ""}
                    onChange={(e) => handleChange("decision_maker", e.target.value)}
                    sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
                  >
                    {decisionMakers?.map((m) => <MenuItem key={m.id} value={m.id} sx={{ fontSize: '0.9rem' }}>{m.name}</MenuItem>)}
                  </TextField>
                )}
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth label="Authorized Consent Provider" variant="outlined"
                  value={formData.consent_provider || ""}
                  onChange={(e) => handleChange("consent_provider", e.target.value)}
                  sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
                />
              </Grid>

              <Grid size={12}>
                <Box sx={{ p: 3, bgcolor: "#fff", borderRadius: "20px", border: "2px dashed #cbd5e1" }}>
                  <Typography variant="caption" sx={{ fontWeight: 900, color: "#64748b", mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BorderColor fontSize="inherit" /> DIGITAL SIGNATURE
                  </Typography>
                  <SignatureField
                    label="Draw new signature here"
                    value={formData.signature instanceof File ? formData.signature : null}
                    onChange={(val) => handleChange("signature", val)}
                  />
                </Box>
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth multiline rows={2} variant="filled"
                  label="Alternative authorization method (if not signed)"
                  value={formData.consent_not_signed_method || ""}
                  onChange={(e) => handleChange("consent_not_signed_method", e.target.value)}
                  sx={inputStyle}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BestInterestsSection;